import { call, cancel, fork, put, select, take } from 'redux-saga/effects';

import * as ProgramDaysApi from 'api/programDays';
import * as ProgramItemsApi from 'api/programItems';

import { routes } from 'defs';
import { EventCustomWarningCodes, EventLevels, EventTypes } from 'defs/events';
import { DATA_BAG } from 'defs/offlineAvailability';
import { POOL_TYPE } from 'defs/pools';

import { log } from 'helper/debug';

import * as poolSagas from 'sagas/pools';
import * as programitemsSagas from 'sagas/programItems';

import { actionCreators as connectionAC, getIsOnline } from 'store/connection';
import { actionCreators as eventsAC } from 'store/events';
import {
	actionTypes as loginAT,
	getHasAuthenticationLoaded,
	getIsAuthenticated,
	getIsMigrosAuthenticated,
} from 'store/login';
import { actionCreators as offlineAvailabilityAC } from 'store/offlineAvailability';
import { actionCreators as poolAC, getPool } from 'store/pools';
import { actionCreators as programDaysAC, getHasDayLoaded, getTodayDayNumber } from 'store/programDays';
import { actionTypes as programDaysActionTypes } from 'store/programDays';
import { actionCreators as programItemsAC, getItemContentIdentifiersByDayNumber } from 'store/programItems';
import { actionTypes as programsAT, getHasLoadedActivePrograms } from 'store/programs';
import { getActiveProgram } from 'store/programs';
import { getRouterHistory } from 'store/router';
import { actionCreators as viewAC } from 'store/view';
import { actionTypes as viewActionTypes } from 'store/view';

import { appendDynamicSpringProducts } from 'sagas/programFeedView.utils';
import { GetProgramItemsByContentIdentifiers } from 'types/services/magnolia/GetProgramItemsByContentIdentifiers';
import { GetProgramDayForDayNumberType } from 'types/services/spring/GetProgramDayForDayNumber';
import { GetProgramDayForTodayType } from 'types/services/spring/GetProgramDayForToday';

function* waitForActiveProgram() {
	yield take(programsAT.REQUEST_ACTIVE_SUCCESS);

	const activeProgram = yield (select(getActiveProgram) as any) as ReturnType<typeof getActiveProgram>;
	if (!activeProgram) {
		return false;
	}
	return activeProgram;
}

export function* _loadProgramFeedView(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkFetchRequestError());
	}

	if (action.view === routes.today.indexRoute.id) {
		try {
			const isMigrosAuthenticated = yield select(getIsMigrosAuthenticated);
			if (!isMigrosAuthenticated) {
				return false;
			}

			yield put(viewAC.doRequestViewLoading(routes.today.indexRoute.id, true));

			const isAuthenticated = yield select(getIsAuthenticated);
			const hasAuthenticationLoaded = yield select(getHasAuthenticationLoaded);
			if (!isAuthenticated && !hasAuthenticationLoaded) {
				yield take(loginAT.LOGIN_STATUS_KNOWN);
			}

			yield fork(programitemsSagas.loadOptionalItems);

			let activeProgram = yield select(getActiveProgram);

			const hasLoadedActivePrograms = yield select(getHasLoadedActivePrograms);
			if (hasLoadedActivePrograms && !activeProgram) {
				return;
			}

			if (!activeProgram) {
				activeProgram = yield waitForActiveProgram();
				if (!activeProgram) {
					return;
				}
			}

			let dayNumber: any = action.params && action.params.dayNumber ? action.params.dayNumber : null;

			if (
				activeProgram &&
				activeProgram.todayDayNumber === 0 &&
				(dayNumber === null || dayNumber > activeProgram.numberOfDays)
			) {
				dayNumber = activeProgram.numberOfDays;
				const history = yield select(getRouterHistory);
				history.replace(`${routes.today.indexRoute.href}/${dayNumber}`);
				return;
			}

			let day: GetProgramDayForTodayType | GetProgramDayForDayNumberType;

			if (dayNumber === null) {
				day = yield call(ProgramDaysApi.fetchActiveProgramDayToday, activeProgram.id);
				yield put(programDaysAC.doRequestTodaySuccess(day));
				dayNumber = yield select(getTodayDayNumber);
			} else {
				day = yield call(ProgramDaysApi.fetchProgramDay, activeProgram.id, dayNumber);
				yield put(programDaysAC.doRequestDaySuccess(day));
				dayNumber = day.dayNumber;
			}

			const itemContentIdentifiers = yield select(getItemContentIdentifiersByDayNumber, dayNumber);
			const programItemsContents = (yield call(
				ProgramItemsApi.fetchProgramItemsContents,
				itemContentIdentifiers
			) as any) as GetProgramItemsByContentIdentifiers | void;

			if (programItemsContents) {
				yield put(
					programItemsAC.doRequestProgramItemsContentsSuccess(
						programItemsContents.concat(yield appendDynamicSpringProducts(day))
					)
				);
			}

			const DataBagKeyType = dayNumber === null ? DATA_BAG.TODAY_FEED_VIEW : DATA_BAG.TODAY_SELECTED_VIEW;

			yield put(
				offlineAvailabilityAC.doRegisterDataBag(DataBagKeyType, {
					day: dayNumber,
				})
			);

			yield put(viewAC.doRequestViewLoading(routes.today.indexRoute.id, false));

			const workoutPoolItems = yield select(getPool as any, POOL_TYPE.WORKOUT);
			if (workoutPoolItems.items.length === 0) {
				yield fork(poolSagas.getPool, poolAC.doRequestPool(POOL_TYPE.WORKOUT));
			}
		} catch (err) {
			/*
			 * Only add notification on unauthorized
			 * @see PULSWM - 1627
			 */
			if (!(err.toString().search('status code 401') >= 0)) {
				log('showing error since it is not an 401 error');
				yield put(
					eventsAC.doAddEvents([
						{
							eventLevel: EventLevels.NotAvailable,
							code: EventCustomWarningCodes.ProgramFeedLoading,
							eventType: EventTypes.Warning,
							payload: { programId: action.programId },
						},
					] as any)
				);
			}

			yield put(viewAC.doRequestViewFailure(routes.today.indexRoute.id));
		}
	}
}
export function* loadProgramFeedView(action: Object) {
	const bgSyncTask: any = yield fork(_loadProgramFeedView, action);
	yield take(viewActionTypes.REQUEST_VIEW);

	yield cancel(bgSyncTask);
}

export function* _loadProgramFeedDay(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkFetchRequestError());
	}

	try {
		const isAuthenticated = yield select(getIsAuthenticated);
		const hasAuthenticationLoaded = yield select(getHasAuthenticationLoaded);

		if (!isAuthenticated && !hasAuthenticationLoaded) {
			yield take(loginAT.LOGIN_STATUS_KNOWN);
		}

		let activeProgram = yield select(getActiveProgram);
		if (!activeProgram) {
			yield take(programsAT.REQUEST_ACTIVE_SUCCESS);
			activeProgram = yield select(getActiveProgram);
		}

		let dayNumber: number;
		if (!action.dayNumber) {
			dayNumber = yield select(getTodayDayNumber);
		} else {
			dayNumber = action.dayNumber;
		}

		const hasDayLoaded = yield select(getHasDayLoaded, dayNumber);

		if (!hasDayLoaded) {
			yield put(viewAC.doRequestViewLoading(routes.today.indexRoute.id, true));

			let currentDay: GetProgramDayForTodayType | GetProgramDayForDayNumberType;
			if (!action.dayNumber) {
				currentDay = yield (call(
					ProgramDaysApi.fetchActiveProgramDayToday,
					activeProgram.id
				) as any) as GetProgramDayForTodayType;
			} else {
				currentDay = yield (call(
					ProgramDaysApi.fetchProgramDay,
					activeProgram.id,
					action.dayNumber
				) as any) as GetProgramDayForDayNumberType;
			}

			if (!currentDay || !currentDay.dayNumber) {
				return;
			}

			yield put(programDaysAC.doRequestDaySuccess(currentDay));

			const itemContentIdentifiers = yield (select(
				getItemContentIdentifiersByDayNumber,
				currentDay.dayNumber
			) as any) as string;
			const programItemsContents = yield call(ProgramItemsApi.fetchProgramItemsContents, itemContentIdentifiers);

			if (programItemsContents) {
				yield put(
					programItemsAC.doRequestProgramItemsContentsSuccess(
						programItemsContents.concat(yield appendDynamicSpringProducts(currentDay))
					)
				);
			}

			yield put(viewAC.doRequestViewLoading(routes.today.indexRoute.id, false));
		} else {
			yield put(viewAC.doRequestViewLoading(routes.today.indexRoute.id, false));
		}
	} catch (err) {
		/**
		 * @see PULSWM - 1627
		 */
		log(`error loading program feed day. caught following error ${err}`);
		if (!(err.toString().search('status code 401') >= 0)) {
			yield put(
				eventsAC.doAddEvents([
					{
						eventLevel: EventLevels.NotAvailable,
						code: EventCustomWarningCodes.ProgramFeedLoading,
						eventType: EventTypes.Warning,
						payload: { programId: action.programId },
					},
				] as any)
			);
		}
		yield put(viewAC.doRequestViewFailure(routes.today.indexRoute.id));
	}
}
export function* loadProgramFeedDay(action: any) {
	const bgSyncTask = yield fork(_loadProgramFeedDay, action);
	yield take(programDaysActionTypes.CHANGE_CURRENT_DAY);

	yield cancel(bgSyncTask);
}
